import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, BookMarked } from "lucide-react";
import { CPT } from "@/constants/testIds";
import { CPT_SYSTEM, listCptCodes } from "@/lib/cptApi";
import "@/styles/emr-catalog.css";

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join("; ");
  return "Unable to load CPT terminology.";
}

export default function CptPage() {
  const [items, setItems] = useState([]);
  const [system, setSystem] = useState(CPT_SYSTEM);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listCptCodes({ active: true });
        if (cancelled) return;
        setItems(data.items || []);
        setSystem(data.system || CPT_SYSTEM);
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="emr-catalog" data-testid={CPT.page}>
      <div className="emr-catalog__pagehead">
        <h1 className="emr-catalog__title" data-testid={CPT.title}>
          CPT Terminology
        </h1>
      </div>

      <div className="emr-catalog__workspace">
        <div className="emr-catalog__side">
          <section className="card emr-catalog__section emr-catalog__purpose">
            <h2 className="emr-catalog__section-title">Purpose</h2>
            <p className="emr-catalog__copy">
              Development/demo CPT master for future Encounter procedure coding. Not a patient
              procedure list and not a billing screen.
            </p>
          </section>

          <section
            data-testid={CPT.banner}
            className="card emr-catalog__section emr-catalog__notice emr-catalog__section--notice"
          >
            <h2 className="emr-catalog__section-title">Reference data</h2>
            <p className="emr-catalog__copy">
              Small demo subset only. Each row is a FHIR Coding ({system}). CPT® is copyright the
              American Medical Association.
            </p>
          </section>
        </div>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">CPT codes</h2>
          {error ? (
            <div data-testid={CPT.errorState} className="emr-catalog__error">
              <AlertCircle className="emr-catalog__error-icon" aria-hidden="true" />
              <p className="emr-catalog__error-copy">{error}</p>
              <Button
                variant="outline"
                className="btn emr-catalog__retry"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          ) : loading ? (
            <div data-testid={CPT.loading} className="emr-catalog__status-msg">
              Loading CPT terminology…
            </div>
          ) : !items.length ? (
            <div data-testid={CPT.emptyState} className="emr-catalog__empty">
              <BookMarked className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>No demo CPT codes are seeded yet.</p>
            </div>
          ) : (
            <div className="emr-catalog__table-wrap">
              <table data-testid={CPT.table} className="striped highlight emr-catalog__table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Official Description</th>
                    <th>Category</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row) => (
                    <tr key={row.id || row.code} data-testid={`${CPT.row}-${row.code}`}>
                      <td className="font-fhir emr-catalog__name">{row.code}</td>
                      <td>{row.display}</td>
                      <td className="emr-catalog__muted">{row.category || "—"}</td>
                      <td>
                        {row.active !== false ? (
                          <span className="emr-catalog__status emr-catalog__status--active">Yes</span>
                        ) : (
                          <span className="emr-catalog__status emr-catalog__status--inactive">No</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
